import React, { useMemo, useRef, useEffect, useState } from 'react';
import * as d3 from 'd3';
import InteractionButtons from './InteractionButtons';
import { formatValue } from '../KPIComponent/formatUtils';
import { ACTOR_CATEGORIES } from '../../constants';
import { Colors } from '../../constants/colors';
import { ButtonGroup, Button, Box } from '@mui/material';
import './InteractionChart.css';

// Create a mapping of category names to colors using CSS variables
export const CATEGORY_COLOR_MAP = Object.fromEntries(
  ACTOR_CATEGORIES.map(cat => {
    const categoryMap = {
      'Civilians': 'civilians',
      'External/Other forces': 'external',
      'Identity militia': 'identity',
      'Political militia': 'political',
      'Protesters': 'protesters',
      'Rebel group': 'rebel',
      'Rioters': 'rioters',
      'State forces': 'state'
    };
    const colorVar = `--color-actor-${categoryMap[cat.name]}`;
    return [cat.name, getComputedStyle(document.documentElement).getPropertyValue(colorVar).trim()];
  })
);

const InteractionChart = ({ data, selectedTypes, onActorSelect, selectedActor, selectedCategories, onCategoryToggle }) => {
  const [mode, setMode] = useState('events'); // 'events' or 'fatalities'
  
  // Add debug logging to understand the data we're receiving
  useEffect(() => {
    console.log(`[InteractionChart] Received ${data?.length || 0} events`);
    if (data?.length > 0) {
      // Log a sample of the data to check actor fields
      const sample = data.slice(0, 3);
      console.log('[InteractionChart] Data sample:', sample);
      
      // Count events with actors and interactions
      const withActors = data.filter(e => e.actor1 && e.actor2).length;
      const withInteractions = data.filter(e => e.inter1 && e.inter2).length;
      console.log(`[InteractionChart] Events with actors: ${withActors}, with interactions: ${withInteractions}`);
    }
  }, [data]);
  
  // Replace the existing categoryColorMap computation with the constant mapping
  const categoryColorMap = CATEGORY_COLOR_MAP;

  // Create a complete set of categories with counts (including zeros)
  const categoryData = useMemo(() => {
    const counts = {};
    const fatalities = {}; // Add fatalities tracking
    
    // Initialize all categories with 0
    ACTOR_CATEGORIES.forEach(cat => {
      counts[cat.name] = 0;
      fatalities[cat.name] = 0;
    });
    
    // Count actual occurrences and fatalities
    data.forEach(event => {
      if (event.inter1) {
        counts[event.inter1] = (counts[event.inter1] || 0) + 1;
        fatalities[event.inter1] = (fatalities[event.inter1] || 0) + (event.fatalities || 0);
      }
      if (event.inter2) {
        counts[event.inter2] = (counts[event.inter2] || 0) + 1;
        fatalities[event.inter2] = (fatalities[event.inter2] || 0) + (event.fatalities || 0);
      }
    });
    
    return ACTOR_CATEGORIES.map(cat => ({
      name: cat.name,
      itemStyle: { color: cat.colour },
      count: counts[cat.name],
      fatalities: fatalities[cat.name]
    }));
  }, [data]);

  // Group actors by category
  const actorsByCategory = useMemo(() => {
    const groupedActors = {};
    
    // Initialize categories from actual data
    Object.keys(categoryColorMap).forEach(category => {
      groupedActors[category] = new Set();
    });

    // Add debug check
    if (!data || data.length === 0) {
      console.warn('[InteractionChart] No data provided to actor grouping');
      return groupedActors;
    }

    // Add actors to their categories only if they have interactions
    data.forEach(event => {
      if (!event) return;
      
      const { actor1, actor2, inter1, inter2 } = event;
      
      // Skip events with missing actor or interaction information
      if (!actor1 || !actor2 || !inter1 || !inter2) {
        return;
      }
      
      // Now add actors to their categories
      groupedActors[inter1]?.add(actor1);
      groupedActors[inter2]?.add(actor2);
    });

    // Remove empty categories and actors with no interactions
    Object.keys(groupedActors).forEach(category => {
      if (groupedActors[category].size === 0) {
        delete groupedActors[category];
      }
    });

    return groupedActors;
  }, [data, categoryColorMap]);

  // Flat list of actors with categories
  const actorsWithCategories = useMemo(() => {
    const result = [];
    Object.entries(actorsByCategory).forEach(([category, actors]) => {
      actors.forEach(actor => {
        result.push({ actor, category });
      });
    });
    return result;
  }, [actorsByCategory]);

  // Filter data based on selected categories
  const filteredData = useMemo(() => {
    if (selectedCategories.length > 0) {
      return data.filter(d =>
        selectedCategories.includes(d.inter1) ||
        selectedCategories.includes(d.inter2)
      );
    }
    return data;
  }, [data, selectedCategories]);

  // Build matrix for chord diagram: each index corresponds to an actor
  const [matrix, actorsArray] = useMemo(() => {
    // Handle empty data case
    if (!filteredData || filteredData.length === 0 || Object.keys(actorsByCategory).length === 0) {
      console.warn('[InteractionChart] Insufficient data for building matrix');
      return [[], []];
    }
    
    const actorCategoryMap = new Map();
    Object.entries(actorsByCategory).forEach(([category, actors]) => {
      actors.forEach(actor => {
        actorCategoryMap.set(actor, category);
      });
    });

    // Make sure we have actors before proceeding
    if (actorCategoryMap.size === 0) {
      console.warn('[InteractionChart] No valid actors found for matrix');
      return [[], []];
    }

    const actorToIndex = new Map();
    const actorsArray = [];
    let actorIndex = 0;

    actorCategoryMap.forEach((category, actor) => {
      actorToIndex.set(actor, actorIndex);
      actorsArray.push({ 
        actor, 
        category,
        totalEvents: 0,
        totalFatalities: 0
      });
      actorIndex += 1;
    });

    const size = actorsArray.length;
    // Matrix needs at least 2 actors to build properly
    if (size < 2) {
      console.warn('[InteractionChart] Not enough actors for matrix, need at least 2');
      return [[], []];
    }
    
    const m = Array.from({ length: size }, () => Array(size).fill(0));

    // First pass: count total events and fatalities for each actor
    filteredData.forEach(event => {
      const i = actorToIndex.get(event.actor1);
      const j = actorToIndex.get(event.actor2);
      if (i != null && j != null) {
        actorsArray[i].totalEvents += 1;
        actorsArray[j].totalEvents += 1;
        if (event.fatalities) {
          actorsArray[i].totalFatalities += event.fatalities;
          actorsArray[j].totalFatalities += event.fatalities;
        }
      }
    });

    // Second pass: build the matrix based on mode
    filteredData.forEach(event => {
      const i = actorToIndex.get(event.actor1);
      const j = actorToIndex.get(event.actor2);
      if (i != null && j != null) {
        // For events mode, just count interactions
        if (mode === 'events') {
          m[i][j] = (m[i][j] || 0) + 1;
          m[j][i] = (m[j][i] || 0) + 1;
        } 
        // For fatalities mode, sum the actual fatalities between specific actor pairs
        else {
          const fatalities = event.fatalities || 0;
          m[i][j] = (m[i][j] || 0) + fatalities;
          m[j][i] = (m[j][i] || 0) + fatalities;
        }
      }
    });

    return [m, actorsArray];
  }, [filteredData, actorsByCategory, mode]);

  const chartRef = useRef();

  // Utility: Truncate text to avoid overly long labels
  const truncateText = (text, maxLength = 50) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength - 3) + '...';
  };

  useEffect(() => {
    // Clear previous chart if any
    if (chartRef.current) {
      // More robust way to clear D3-managed content
      chartRef.current.innerHTML = '';
    }

    if (actorsWithCategories.length === 0 || matrix.length === 0) return;

    const width = 1000;
    const height = 900;
    const margin = 0;
    const outerRadius = Math.min(width, height) * 0.3;
    const innerRadius = outerRadius * 0.9;
    const labelDistance = 36;

    // Create SVG
    const svg = d3.select(chartRef.current)
      .append('svg')
      .attr('viewBox', `${-margin} ${-margin} ${width + 2 * margin} ${height + 2 * margin}`)
      .style('width', '100%')
      .style('height', '100%')
      .style('font', '11px var(--font-family-base)');

    const container = svg.append('g')
      .attr('transform', `translate(${width / 2},${height / 2})`);

    // Tooltip
    const tooltip = d3.select(chartRef.current)
      .append('div')
      .style('position', 'absolute')
      .style('background', 'rgba(255,255,255,0.95)')
      .style('border-radius', '4px')
      .style('padding', '8px 12px')
      .style('font-size', '12px')
      .style('font-family', 'inherit')
      .style('visibility', 'hidden')
      .style('pointer-events', 'none')
      .style('box-shadow', '0 2px 8px rgba(0,0,0,0.15)')
      .style('z-index', '100');

    // Create chord layout
    const chord = d3.chord()
      .padAngle(0.04)
      .sortSubgroups(d3.descending)(matrix);

    const arc = d3.arc()
      .innerRadius(innerRadius)
      .outerRadius(innerRadius + 20);

    const ribbon = d3.ribbon()
      .radius(innerRadius);

    // Determine categories order and their index
    const uniqueCategories = [...new Set(actorsWithCategories.map(d => d.category))];
    const categoryIndex = cat => uniqueCategories.indexOf(cat);

    const color = d => {
      const cat = actorsWithCategories[d.index].category;
      return categoryColorMap[cat];
    };

    // Opacity logic based on selectedActor
    const arcOpacity = d => {
      if (!selectedActor) return 0.7;
      const a = actorsWithCategories[d.index].actor;
      return a === selectedActor ? 1 : 0.2;
    };

    const chordOpacity = d => {
      if (!selectedActor) return 0.7;
      const sourceActor = actorsWithCategories[d.source.index].actor;
      const targetActor = actorsWithCategories[d.target.index].actor;
      return (sourceActor === selectedActor || targetActor === selectedActor) ? 0.8 : 0.1;
    };

    // Draw groups (actors)
    const group = container.append('g')
      .attr('class', 'groups')
      .selectAll('g')
      .data(chord.groups)
      .enter().append('g')
      .style('cursor', 'pointer')
      .on('click', (event, d) => {
        const actor = actorsArray[d.index].actor;
        onActorSelect(actor === selectedActor ? null : actor);
      })
      .on('mouseover', (event, d) => {
        // Highlight chords connected to this actor
        const actorName = actorsWithCategories[d.index].actor;
        container.selectAll('.ribbons path')
          .style('opacity', c => {
            const srcActor = actorsArray[c.source.index].actor;
            const tgtActor = actorsArray[c.target.index].actor;
            return (srcActor === actorName || tgtActor === actorName) ? 1 : 0.1;
          });

        container.selectAll('.groups path')
          .style('opacity', g => {
            const a = actorsArray[g.index].actor;
            return (a === actorName || matrix[d.index][g.index] > 0) ? 1 : 0.2;
          });

        const { actor, category } = actorsArray[d.index];
        const totalEvents = actorsArray[d.index].totalEvents;
        const totalFatalities = actorsArray[d.index].totalFatalities;

        // Position tooltip
        const rect = chartRef.current.getBoundingClientRect();
        tooltip.html(`
          <div style="font-weight:bold; margin-bottom:6px;">${actor}</div>
          <div style="font-size: var(--font-size-md); margin-bottom:4px;">
            Category: <span style="background:${categoryColorMap[category]}; color:white; padding:0 5px; border-radius:3px;">${category}</span>
          </div>
          <div style="font-size: var(--font-size-md); display:flex; justify-content:space-between; margin-bottom:2px;">
            <span>Events:</span>
            <span>${formatValue(Math.round(totalEvents/2), 'thousands')}</span>
          </div>
          <div style="font-size: var(--font-size-md); display:flex; justify-content:space-between;">
            <span>Fatalities:</span>
            <span>${formatValue(Math.round(totalFatalities/2), 'thousands')}</span>
          </div>
        `)
        .style('visibility', 'visible')
        .style('left', (event.clientX - rect.left + 10) + 'px')
        .style('top', (event.clientY - rect.top - 10) + 'px');
      })
      .on('mousemove', event => {
        const rect = chartRef.current.getBoundingClientRect();
        tooltip
          .style('left', (event.clientX - rect.left + 10) + 'px')
          .style('top', (event.clientY - rect.top - 10) + 'px');
      })
      .on('mouseout', () => {
        tooltip.style('visibility', 'hidden');
        // Reset opacity
        container.selectAll('.ribbons path').style('opacity', chordOpacity);
        container.selectAll('.groups path').style('opacity', arcOpacity);
      });

    // Draw arc for each actor
    group.append('path')
      .attr('d', arc)
      .style('fill', d => {
        const category = actorsArray[d.index].category;
        return categoryColorMap[category];
      })
      .style('stroke', d => d3.rgb(categoryColorMap[actorsArray[d.index].category]).darker(0.2))
      .style('opacity', arcOpacity);

    // Create labelData with angles and actor data
    const labelData = chord.groups.map((d) => {
      const actorData = actorsArray[d.index]; // actor, category, totalInteractions
      const angle = (d.startAngle + d.endAngle) / 2;
      return { ...d, actorData, angle };
    });

    // Sort labels by total interactions (descending)
    labelData.sort((a, b) => b.actorData.totalEvents - a.actorData.totalEvents);

    // Initialize an array to keep track of placed labels and define minimum label angle
    const placedLabels = [];
    const minLabelPixelSeparation = 8.7; // Minimum pixel separation between labels
    const minLabelAngle = minLabelPixelSeparation / (innerRadius + labelDistance);

    // Create a group for labels
    const labelsGroup = container.append('g').attr('class', 'labels');

    labelData.forEach(label => {
      let canPlace = true;
      for (const placedLabel of placedLabels) {
        let angleDiff = Math.abs(label.angle - placedLabel.angle);
        angleDiff = angleDiff > Math.PI ? 2 * Math.PI - angleDiff : angleDiff;
        if (angleDiff < minLabelAngle) {
          canPlace = false;
          break;
        }
      }
      if (canPlace) {
        placedLabels.push(label);

        labelsGroup.append('text')
          .attr('dy', '0.35em')
          .attr('transform', `
            rotate(${(label.angle * 180 / Math.PI - 90)})
            translate(${innerRadius + labelDistance})
            ${label.angle > Math.PI ? 'rotate(180)' : ''}
          `)
          .style('text-anchor', label.angle > Math.PI ? 'end' : 'start')
          .style('font-size', '9.2px')
          .style('font-family', 'var(--font-family-base)')
          .text(truncateText(label.actorData.actor))
          .style('opacity', !selectedActor ? 1 : (label.actorData.actor === selectedActor ? 1 : 0.3));
      }
    });

    // Draw ribbons (connections)
    const ribbonSelection = container.append('g')
      .attr('class', 'ribbons')
      .selectAll('path')
      .data(chord)
      .enter().append('path')
      .attr('d', ribbon)
      .style('fill', d => color(d.target))
      .style('stroke', d => d3.rgb(color(d.target)).darker(0.2))
      .style('opacity', chordOpacity)
      .on('mouseover', (event, d) => {
        // Highlight only this chord
        const source = actorsWithCategories[d.source.index];
        const target = actorsWithCategories[d.target.index];

        ribbonSelection.style('opacity', r => r === d ? 1 : 0.1);

        container.selectAll('.groups path')
          .style('opacity', g => {
            const actorName = actorsWithCategories[g.index].actor;
            return (actorName === source.actor || actorName === target.actor) ? 1 : 0.3;
          });

        // Calculate events and fatalities between these specific actors
        const eventsCount = filteredData.filter(e => 
          (e.actor1 === source.actor && e.actor2 === target.actor) || 
          (e.actor1 === target.actor && e.actor2 === source.actor)
        ).length;

        const fatalitiesCount = filteredData
          .filter(e => 
            (e.actor1 === source.actor && e.actor2 === target.actor) || 
            (e.actor1 === target.actor && e.actor2 === source.actor)
          )
          .reduce((sum, e) => sum + (e.fatalities || 0), 0);

        const rect = chartRef.current.getBoundingClientRect();
        tooltip.html(`
          <div style="font-weight:bold; margin-bottom:6px;">
            ${source.actor} ↔ ${target.actor}
          </div>
          <div style="font-size: var(--font-size-md); margin-bottom:4px;">
            ${source.category} ↔ ${target.category}
          </div>
          <div style="font-size: var(--font-size-md); display:flex; justify-content:space-between; margin-bottom:2px;">
            <span>Events:</span>
            <span>${formatValue(eventsCount, 'thousands')}</span>
          </div>
          <div style="font-size: var(--font-size-md); display:flex; justify-content:space-between;">
            <span>Fatalities:</span>
            <span>${formatValue(fatalitiesCount, 'thousands')}</span>
          </div>
        `)
        .style('visibility', 'visible')
        .style('left', (event.clientX - rect.left + 10) + 'px')
        .style('top', (event.clientY - rect.top - 10) + 'px');
      })
      .on('mousemove', event => {
        const rect = chartRef.current.getBoundingClientRect();
        tooltip
          .style('left', (event.clientX - rect.left + 10) + 'px')
          .style('top', (event.clientY - rect.top - 10) + 'px');
      })
      .on('mouseout', () => {
        tooltip.style('visibility', 'hidden');
        ribbonSelection.style('opacity', chordOpacity);
        container.selectAll('.groups path').style('opacity', arcOpacity);
      });

  }, [actorsWithCategories, mode, categoryColorMap, selectedActor, matrix, actorsArray, onActorSelect]);

  return (
    <div className="interaction-chart">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <h3 style={{ margin: 0 }}>Actor Interaction Network</h3>
        <ButtonGroup variant="outlined" size="small">
          <Button 
            onClick={() => setMode('events')} 
            variant={mode === 'events' ? 'contained' : 'outlined'}
          >
            Events
          </Button>
          <Button 
            onClick={() => setMode('fatalities')} 
            variant={mode === 'fatalities' ? 'contained' : 'outlined'}
          >
            Fatalities
          </Button>
        </ButtonGroup>
      </div>
      
      <InteractionButtons 
        categories={categoryData}
        categoryColorMap={categoryColorMap}
        selectedCategories={selectedCategories}
        onCategoryToggle={onCategoryToggle}
        mode={mode} // Pass mode to InteractionButtons
      />
      
      <div style={{ marginTop: '10px', position:'relative' }}>
        {/* D3 chart container, visibility controlled by data/matrix state */}
        <div 
          ref={chartRef} 
          style={{ 
            display: (data && data.length > 0 && matrix && matrix.length > 0) ? 'block' : 'none',
            width: '100%', // Ensure it takes up space for D3
            height: '500px' // Example height, adjust as needed or make responsive
          }}
        >
          {/* D3 chord diagram will be appended here by the useEffect hook */}
        </div>

        {/* Show empty state message when no data */}
        {(!data || data.length === 0) && (
          <div className="interaction-chart-empty">
            <p>No actor interaction data available for the selected filters</p>
          </div>
        )}
        
        {/* Show warning message when data exists but no valid interactions and chart isn't shown */}
        {data && data.length > 0 && (!matrix || matrix.length === 0) && (
          <div className="interaction-chart-empty">
            <p>The data doesn't contain valid actor interactions for a chart</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default InteractionChart;
